import { type ClassValue } from 'clsx';
import { isStringLiteral } from '@enonic/js-utils/value/isStringLiteral';
import { type IconDefinition } from '@fortawesome/pro-light-svg-icons';
import { type FontAwesomeIconProps } from '@fortawesome/react-fontawesome';
import { ErrorMessage } from '@hdir/ui/error-message';
import React, {
	useEffect,
	useId,
	useRef,
} from 'react';
import {
	type FieldError,
	type FieldPath,
	type FieldPathValue,
	type FieldValues,
	type UseFormSetValue,
	useController,
	useWatch,
} from 'react-hook-form';

import {
	type ElementProps,
	Span,
} from '/react4xp/components/Element';
import { InlineRow, Stack } from '/react4xp/components/Flex';
import { Fieldset } from '/react4xp/components/react-hook-form/Fieldset';
import { useFormContext } from '/react4xp/components/react-hook-form/FormContext';
import { isShowIfFunction, type ShowIfFunction } from '/react4xp/components/react-hook-form/isShowIfFunction';

import { ButtonRadio } from './ButtonRadio';


type FieldsetAttributes = React.FieldsetHTMLAttributes<HTMLFieldSetElement>;

export interface ButtonRadioGroupOption {
	icon?: IconDefinition;
	iconColor?: FontAwesomeIconProps['color'];
	label?: string;
	value: string;
}

export type ButtonRadioGroupProps<
	TFieldValues extends FieldValues = FieldValues,
	TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> = Omit<FieldsetAttributes, 'className'|'color'|'name'|'translate'> & {
	afterClick?: (params: {
		newValue: string | string[] | null | undefined;
		setValue: UseFormSetValue<TFieldValues>;
	}) => void
	error?: string;
	errorSummaryLabel?: string;
	description?: string;
	gap?: ElementProps['gap'];
	multiple?: boolean;
	name: TName;
	options: ButtonRadioGroupOption[];
	label?: string;
	className?: ClassValue;
	showIf?: ShowIfFunction<TFieldValues>;
};


export function ButtonRadioGroup<
	TFieldValues extends FieldValues = FieldValues,
	TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>({
	afterClick,
	description,
	disabled: disabledProp,
	error: errorProp,

	// This can only be used to customize horizontal gap on (currently all breakpoints)
	gap = 'sm',

	name,
	options,
	label,
	errorSummaryLabel = isStringLiteral(label) ? label : undefined,
	showIf,
	...otherProps
}: ButtonRadioGroupProps<TFieldValues, TName>): JSX.Element | null {
	const id = useId(); // Generate unique IDs for accessibility
	const descriptionId = useId();

	const {
		control,
		getFieldType,
		getRequired,
		setValue,
		setErrorSummaryLabel,
		setFocusRef,
		setScrollRef,
	} = useFormContext<TFieldValues>();

	const isRequired: boolean = getRequired
		? getRequired(name)
		: false;

	const {
		field: {
			disabled,
			onChange: useControllerOnChange,
			// ref: useControllerFieldRef, // I keep my own REAL refs for ErrorSummary.
			value: groupValue,
		},
		fieldState: {
			error: errorState,
		},
	} = useController<TFieldValues, TName>({
		control,
		disabled: disabledProp,
		name,
	});

	const error = errorState || errorProp;

	const focusRef = useRef<HTMLInputElement>();
	const scrollRef = useRef<HTMLLegendElement>();
	const typedGroupValue = groupValue as string;

	const fieldType = getFieldType({ fieldName: name });
	// undefined doesn't work? It does on enums at least. #70651
	// null causes an optional field to become required! See #68014
	// but '' makes a required field, optional!
	const unsetValue = (isRequired
		? null // For required fields.
		: fieldType === 'enum' // For optional fields.
			? undefined // For optional enums, to avoid valition errors. See #70651
			: '' // For optional strings.
	);

	useEffect(() => {
		if (errorSummaryLabel) {
			setErrorSummaryLabel({field: name, label: errorSummaryLabel});
		}
	},[errorSummaryLabel]);

	useEffect(() => {
		if (focusRef.current) {
			setFocusRef({ field: name, el: focusRef.current });
		}
	},[focusRef.current]);

	useEffect(() => {
		if (scrollRef.current) {
			setScrollRef({ field: name, el: scrollRef.current });
		}
	},[scrollRef.current]);

	if (isShowIfFunction(showIf) && !showIf({
		// eslint-disable-next-line @typescript-eslint/no-unsafe-return
		watch: (watchParams) => useWatch({...watchParams, control}) as FieldPathValue<TFieldValues, FieldPath<TFieldValues>>,
	})) {
		return null;
	}

	return (
		<Fieldset
			{...otherProps}
			borderless
			className='text-md'
			disabled={disabled}

			// No no, let's not use horizontal gap as vertical gap!
			// gap={gap}

			legend={label}
			legendRef={scrollRef}
			name={name}
			required={isRequired}
		>
			<Span
				className={[
					'text-sm',
					{ 'text-neutral-disabled': disabled },
				]}
				id={descriptionId}
			>{description}</Span>
			<Stack
				aria-describedby={descriptionId}
				className='items-start'
				role='group'
			>
				<InlineRow
					boxShadow={error ? 'after:insetRingMdError' : undefined}
					className={[
						'after:content-[""] after:absolute after:h-full after:pointer-events-none after:w-full after:z-10',
						'flex-wrap relative',
					]}
					gap={gap}
				>
					{
						options.map(({
							icon,
							iconColor,
							label,
							value,
						}, index) => {
							const checked = typedGroupValue === value;
							function onChange(): void {
								const newValue = checked ? unsetValue : value;
								useControllerOnChange(newValue);
								if (afterClick) afterClick({
									newValue,
									setValue,
								});
							};
							return (
								<ButtonRadio
									checked={checked}
									disabled={disabled}
									error={!!error}
									icon={icon}
									iconColor={iconColor}
									id={`${id}-${index}`}
									name={name}
									label={label}
									onChange={onChange}
									onKeyUp={(event) => {
										if (
											event.code === 'Enter'
											|| event.code === 'Space'
										) {
											event.preventDefault(); // Make sure Space can unset value.
											onChange();
										}
									}}
									ref={(inputElement) => {
										if (inputElement) {
											if (index === 0) {
												focusRef.current = inputElement;
											}
										}
									}}
								/>
							);
						})
					}
				</InlineRow>
			</Stack>
			{error ? <ErrorMessage>
				{isStringLiteral(error) ? error : (error as FieldError).message}
			</ErrorMessage>: null}
		</Fieldset>
	);
};
